import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { ENTITIES, IMAGES, MANIFESTO } from "@/data/content";
import { MapPin } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About — Vigarbiz"
        siteName="Vigarbiz"
        description="Two continents, one obsession: growth. Meet Vigarbiz ITES Pvt Ltd (Pune) and Vigarbiz Inc (Fremont), the full-service digital marketing agency behind 120+ scaled brands."
      />
      <PageHeader
        overline="About Vigarbiz"
        lines={["Two continents.", "One obsession:", <span key="g" className="text-brand">Growth.</span>]}
        description="Vigarbiz was built on a simple arbitrage: world-class strategy at US speed, world-class execution at India scale. One team, one P&L of outcomes, zero handoffs lost in translation."
      />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src={IMAGES.team}
                  alt="Vigarbiz strategy team collaborating in the studio"
                  className="h-[380px] w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-8 -right-4 hidden w-56 overflow-hidden rounded-2xl border border-brand/40 shadow-[0_0_60px_rgba(255,94,26,0.25)] sm:block">
                <img
                  src={IMAGES.workspace}
                  alt="Overhead view of the Vigarbiz delivery floor"
                  className="h-40 w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">The story</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Built in Pune. Sharpened in Fremont.
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted2">
              <p>
                Vigarbiz ITES Pvt Ltd runs our delivery engine from Pune — analysts, engineers,
                designers and media operators working in deep-focus pods. Vigarbiz Inc anchors client
                strategy from Fremont, California, in the same rooms where growth targets get set.
              </p>
              <p>
                The result is a follow-the-sun agency: briefs written over US breakfast become live
                campaigns by US morning. Our clients get senior thinking and relentless output without
                paying senior-agency bloat for either.
              </p>
              <p>
                We measure ourselves the way our clients do — pipeline, revenue, payback period. Awards
                are nice. ROAS is nicer.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-surface/40 py-20 lg:py-28" data-testid="manifesto-chapters">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">The manifesto</p>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
              How we work
            </h2>
          </Reveal>
          <div className="mt-12 space-y-0">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.05}>
                <div className="grid gap-4 border-t border-white/10 py-10 last:border-b md:grid-cols-[120px_1fr_1.4fr] md:gap-10">
                  <span className="font-mono text-2xl text-brand">{m.num}</span>
                  <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                    {m.title}
                  </h3>
                  <p className="text-base leading-relaxed text-muted2">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="about-entities">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
            One agency. Two legal entities.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-muted2 md:text-lg">
            Contract in dollars or rupees, with the entity that fits your procurement — the team and the
            outcomes stay identical.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {ENTITIES.map((e, i) => (
            <Reveal key={e.key} delay={i * 0.08}>
              <div
                data-testid={`about-entity-${e.key}`}
                className="glow-card h-full rounded-2xl border border-white/10 bg-surface p-6 sm:p-10"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{e.city}</p>
                <h3 className="mt-3 font-display text-2xl font-bold text-white">{e.name}</h3>
                <p className="mt-1 text-sm text-muted2">{e.region}</p>
                <p className="mt-6 flex items-start gap-2 text-sm text-muted2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  {e.address}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBand title="Sound like your kind of agency?" sub="Bring us a target. We'll bring the plan, the team and the first 90 days of momentum." />
    </div>
  );
}
